// ===========================================
// Permission Slips (Admin)
// ===========================================
// The list + tracking view. Mirrors SaveTheDatesPage, which is the closest
// analogue in the codebase.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  usePermissionSlips,
  useDeletePermissionSlip,
  useSendPermissionSlip,
} from '../../hooks/usePermissionSlips';
import { formatUTCDate } from '../../utils/dateUtils';
import type { PermissionSlipStatus } from '../../api/permissionSlips.api';

// === STATUS CONFIGURATION ===

const statusConfig: Record<PermissionSlipStatus, { label: string; bgcolor: string; color: string }> = {
  DRAFT: { label: 'Draft', bgcolor: '#9DA5AF', color: 'white' },
  SCHEDULED: { label: 'Scheduled', bgcolor: '#FFCE00', color: '#080808' },
  SENT: { label: 'Sent', bgcolor: '#4580E4', color: 'white' },
  // FAILED is visually loud on purpose: a failed send is indistinguishable
  // from a delivered one without it, and the slip stops accepting responses.
  FAILED: { label: 'Failed', bgcolor: '#ff4040', color: 'white' },
  CLOSED: { label: 'Closed', bgcolor: '#96DAC9', color: '#080808' },
};

type StatusFilterValue = 'ALL' | PermissionSlipStatus;

const statusTabs: { value: StatusFilterValue; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'SENT', label: 'Sent' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CLOSED', label: 'Closed' },
];

// === MAIN COMPONENT ===

export default function PermissionSlipsPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [sendTargetId, setSendTargetId] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: slips = [], isLoading, isError, error, refetch } = usePermissionSlips(
    statusFilter === 'ALL' ? undefined : { status: statusFilter }
  );
  const deleteMutation = useDeletePermissionSlip();
  const sendMutation = useSendPermissionSlip();

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  const filtered = slips.filter((s) =>
    debouncedSearch ? s.title.toLowerCase().includes(debouncedSearch.toLowerCase()) : true
  );
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    await deleteMutation.mutateAsync(deleteTargetId).catch(() => undefined);
    setDeleteTargetId(null);
  };

  const handleSend = async () => {
    if (!sendTargetId) return;
    await sendMutation.mutateAsync(sendTargetId).catch(() => undefined);
    setSendTargetId(null);
  };

  return (
    <Box>
      <PageHeader
        title="Permission Slips"
        subtitle="Ask families to give permission for activities, excursions and camps"
        actionLabel="New Permission Slip"
        actionIcon={<AddIcon />}
        onAction={() => navigate(`/${schoolSlug}/admin/permission-slips/new`)}
      />

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v: StatusFilterValue) => {
            setStatusFilter(v);
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {statusTabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              size="small"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error & { response?: { data?: { message?: string } } })?.response?.data
            ?.message || 'Failed to load permission slips. Please try again.'}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Event</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <FactCheckIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {debouncedSearch || statusFilter !== 'ALL'
                        ? 'No permission slips match this filter.'
                        : 'No permission slips yet. Create one to ask families for permission.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((slip) => {
                  const cfg = statusConfig[slip.status];
                  const canSend = slip.status === 'DRAFT' || slip.status === 'SCHEDULED';
                  const canDelete = slip.status === 'DRAFT' || slip.status === 'SCHEDULED';
                  return (
                    <TableRow
                      key={slip.id}
                      hover
                      onClick={() => navigate(`/${schoolSlug}/admin/permission-slips/${slip.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{slip.title}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={cfg.label}
                          sx={{ bgcolor: cfg.bgcolor, color: cfg.color }}
                        />
                      </TableCell>
                      <TableCell>{slip.deadline ? formatUTCDate(slip.deadline) : '—'}</TableCell>
                      <TableCell>{slip.Event?.title ?? '—'}</TableCell>
                      <TableCell align="right">
                        {slip._count?.permissionSlipResponses ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {canSend && (
                          <Tooltip title="Send now">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSendTargetId(slip.id);
                              }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTargetId(slip.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <ConfirmDialog
        open={!!deleteTargetId}
        title="Delete permission slip?"
        message="This cannot be undone. A slip that has already been sent, or that has responses, cannot be deleted — close it instead."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTargetId(null)}
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={!!sendTargetId}
        title="Send this permission slip?"
        message="Every family in the audience will be emailed a link to sign. This cannot be unsent."
        confirmLabel="Send"
        onConfirm={handleSend}
        onCancel={() => setSendTargetId(null)}
        loading={sendMutation.isPending}
      />
    </Box>
  );
}
